import { View, Text, Image, TouchableOpacity, StyleSheet } from 'react-native';
import { useRouter, usePathname } from 'expo-router';
import Feather from '@expo/vector-icons/Feather';
import { Paprika } from '../styles/theme';
import { useTranslation } from 'react-i18next';
import { DESKTOP_SIDEBAR_WIDTH } from '../constants/layout';
import { useDispatch, useStore } from 'react-redux';
import type { RootState } from '../redux/store';
import { plannerHome } from '../redux/mealPlanUISlice';
import { setCurrentWeek } from '../redux/mealPlanSlice';
import { currentWeekStart } from '../utils/localDate';
import { trackEvent } from '../services/analyticsService';

/** The official lockup (mark + wordmark), same file the onboarding brand row uses. */
const LOGO = require('../../assets/brand/duchef-logo.png');
const LOGO_HEIGHT = 34;
const LOGO_WIDTH = LOGO_HEIGHT * (330 / 116);

type NavItem = {
  href: string;
  icon: React.ComponentProps<typeof Feather>['name'];
  labelKey: string;
  matchPrefixes: string[];
};

const NAV_ITEMS: NavItem[] = [
  {
    href: '/',
    icon: 'calendar',
    labelKey: 'navigation.myMeals',
    matchPrefixes: ['/', '/day-detail', '/cooking-plan', '/week-recap'],
  },
  {
    href: '/recipes',
    icon: 'book-open',
    labelKey: 'navigation.recipes',
    matchPrefixes: ['/recipes', '/recipe-detail', '/recipe-suggestions', '/delivery-import'],
  },
  {
    href: '/grocery-list',
    icon: 'shopping-cart',
    labelKey: 'navigation.grocery',
    matchPrefixes: ['/grocery-list'],
  },
  {
    href: '/inventory',
    icon: 'package',
    labelKey: 'navigation.pantry',
    matchPrefixes: ['/inventory', '/inventory-camera', '/recognition-results'],
  },
  {
    href: '/settings',
    icon: 'settings',
    labelKey: 'navigation.settings',
    matchPrefixes: ['/settings', '/userProfile', '/editProfile', '/taste-profile', '/preferences'],
  },
];

export function DesktopSidebar() {
  const { t } = useTranslation();
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useDispatch();
  const store = useStore<RootState>();

  /**
   * « Mes repas » on the planner itself used to navigate to the route it was
   * already on, which does nothing: the open drawer, the settings screen and
   * the week shown all live in redux. It now brings the planner home — this
   * week, nothing open.
   */
  const goPlannerHome = () => {
    const { mealPlanUI, mealPlan } = store.getState();
    const thisWeek = currentWeekStart();
    const closed = [
      mealPlanUI.sel !== null && 'drawer',
      mealPlanUI.libFor !== null && 'library',
      mealPlanUI.detailFor !== null && 'detail',
      mealPlanUI.configPhase === 'config' && 'config',
    ].filter(Boolean);
    trackEvent('planner_home_pressed', {
      other_week: mealPlan.currentWeekStart !== thisWeek,
      closed,
    });
    dispatch(plannerHome());
    dispatch(setCurrentWeek(thisWeek));
  };

  const isActive = (item: NavItem) => {
    if (pathname === '/' && item.href === '/') return true;
    return item.matchPrefixes.some(
      (prefix) => prefix !== '/' && pathname.startsWith(prefix)
    );
  };

  return (
    <View style={styles.sidebar}>
      <View style={styles.logo}>
        <Image
          source={LOGO}
          style={styles.logoImage}
          resizeMode="contain"
          accessibilityLabel={t('onboarding.brand')}
        />
      </View>

      <View style={styles.nav}>
        {NAV_ITEMS.map((item) => {
          const active = isActive(item);
          return (
            <TouchableOpacity
              key={item.href}
              style={[styles.navItem, active && styles.navItemActive]}
              onPress={() => (item.href === '/' && active ? goPlannerHome() : router.navigate(item.href as any))}
              testID={`sidebar-${item.labelKey.split('.').pop()}`}
              activeOpacity={0.7}
            >
              <Feather
                name={item.icon}
                size={20}
                color={active ? Paprika.accent : Paprika.textSecondary}
              />
              <Text style={[styles.navLabel, active && styles.navLabelActive]}>
                {t(item.labelKey)}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  sidebar: {
    width: DESKTOP_SIDEBAR_WIDTH,
    backgroundColor: Paprika.background,
    borderRightWidth: 1,
    borderRightColor: Paprika.borderLight,
    paddingTop: 20,
    paddingHorizontal: 12,
  },
  logo: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 16,
    marginBottom: 8,
  },
  logoImage: {
    height: LOGO_HEIGHT,
    width: LOGO_WIDTH,
  },
  nav: {
    gap: 4,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    gap: 12,
  },
  navItemActive: {
    backgroundColor: Paprika.surfaceLowest,
  },
  navLabel: {
    fontSize: 15,
    fontWeight: '500',
    color: Paprika.textSecondary,
  },
  navLabelActive: {
    color: Paprika.accent,
    fontWeight: '600',
  },
});
